import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { motionOn } from '@/lib/motion';

gsap.registerPlugin(ScrollTrigger);

/**
 * <HeroImage src alt />: rounded photo panel for page heroes. Monochrome photo
 * with a violet wash, opened by a clip-path wipe after the headline lands, then
 * drifting on scroll.
 */
export function HeroImage({ src, alt, className = '', loadDelayMs = 650, testId = 'hero-image', caption = 'NIO/STUDIO', location = 'United States' }) {
  const wrap = useRef(null);
  const img = useRef(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!motionOn() || !wrap.current) return undefined;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        wrap.current,
        { clipPath: 'inset(100% 0% 0% 0% round 18px)' },
        { clipPath: 'inset(0% 0% 0% 0% round 18px)', duration: 1.4, ease: 'expo.inOut', delay: loadDelayMs / 1000 }
      );
      gsap.fromTo(img.current, { scale: 1.25 }, { scale: 1.06, duration: 2, ease: 'expo.out', delay: loadDelayMs / 1000 });
      gsap.fromTo(img.current, { yPercent: -5 }, {
        yPercent: 5, ease: 'none',
        scrollTrigger: { trigger: wrap.current, start: 'top bottom', end: 'bottom top', scrub: true },
      });
    });
    return () => ctx.revert();
  }, [loadDelayMs]);

  return (
    <div
      ref={wrap}
      data-testid={testId}
      className={`relative w-full aspect-[5/4] overflow-hidden rounded-[18px] ring-1 ring-white/[0.08] bg-ink-surface ${className}`}
    >
      <img
        ref={img}
        src={src}
        alt={alt}
        onLoad={() => setLoaded(true)}
        loading="eager"
        decoding="async"
        referrerPolicy="no-referrer"
        className="absolute inset-0 h-full w-full object-cover scale-[1.06] transition-opacity duration-500"
        style={{ filter: 'grayscale(0.9) contrast(1.05) brightness(0.8)', opacity: loaded ? 1 : 0 }}
        data-testid={`${testId}-img`}
      />
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-[linear-gradient(200deg,rgba(108,92,231,.38),transparent_55%),linear-gradient(0deg,rgba(10,10,15,.7),transparent_45%)]" />
      <div className="nd-mono pointer-events-none absolute left-5 right-5 bottom-4 flex justify-between !text-[11px]">
        <span className="text-white">{caption}</span>
        <span>{location}</span>
      </div>
    </div>
  );
}
